<script>
import { mapActions, mapGetters } from 'vuex';
import RbxConfirmDialog from '@/shared/reybex/RbxConfirmDialog';
import downloader from '@/mixins/downloader';
import mainViewTabMixin from '@/mixins/mainViewTab';
import snackbarMixin from '@/mixins/snackbar';
import AvContractBasicInfoForm from './components/AvContractBasicInfoForm';
import AvContractObjectAndDurationForm from './components/AvContractObjectAndDurationForm';
import AvContractJobContentForm from './components/AvContractJobContentForm';
import AvContractTypeOfDataForm from './components/AvContractTypeOfDataForm';
import AvContractQualityControlForm from './components/AvContractQualityControlForm';
import AvContractSubcontractToForm from './components/AvContractSubcontractToForm';
import AvContractControlRightsForm from './components/AvContractControlRightsForm';
import AvContractTransferOfficerForm from './components/AvContractTransferOfficerForm';
import AvContractExamForm from './components/AvContractExamForm';
import Documents from './components/Documents';
import Notes from '../notes';
import History from '../history';

export default {
  name: 'AvContractDetails',
  components: {
    AvContractBasicInfoForm,
    AvContractObjectAndDurationForm,
    AvContractJobContentForm,
    AvContractTypeOfDataForm,
    AvContractQualityControlForm,
    AvContractSubcontractToForm,
    AvContractControlRightsForm,
    AvContractTransferOfficerForm,
    AvContractExamForm,
    Documents,
    Notes,
    History,
    RbxConfirmDialog,
  },
  mixins: [downloader, mainViewTabMixin, snackbarMixin],
  data() {
    return {
      query: {},
      params: {},
      avContract: {},
      isLoading: false,
      isSaving: false,
      hasError: false,
      activeTab: 0,
      isDownloading: false,
      isNewVersionConfirmDialogDisplayed: false,
    };
  },
  computed: {
    ...mapGetters('app', ['auth']),
  },
  created() {
    const { query, params } = this.urlParser('/avContract/show/:id?', this.path);

    this.query = query;
    this.params = params;
    this.fetchData();
  },
  methods: {
    ...mapActions('avContractList', ['downloadReport']),
    ...mapActions('avContractDetails', ['fetchAvContract', 'saveAvContract', 'copyAvContract']),
    fetchData() {
      this.isLoading = true;
      this.hasError = false;

      return this.fetchAvContract(this.params.id)
        .then(({ data }) => {
          this.avContract = data;
          this.updateTabTitle({ path: this.path, title: this.avContract.name });
        })
        .catch(() => {
          this.hasError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    save() {
      this.isSaving = true;

      return this.saveAvContract(this.avContract)
        .then(() => {
          this.showSuccessMessage('AV-Vertrag erfolgreich gespeichert!');
        })
        .catch(() => {
          this.showErrorMessage('AV-Vertrag konnte nicht gespeichert werden. Bitte versuche es erneut!');
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    _downloadReport() {
      this.isDownloading = true;
      const self = this;
      this.downloadReport(this.avContract.id)
        .then((response) => {
          this.getDownloadContent(response, 'av_contract_report.pdf');
        }).catch(() => {
          this.showErrorMessage('Beim Erstellen des Berichts ist ein Fehler aufgetreten.');
        }).finally(() => {
          self.isDownloading = false;
        });
    },
    onNewVersionConfirm() {
      this.isNewVersionConfirmDialogDisplayed = false;
      this.isSaving = true;

      this.copyAvContract(this.avContract)
        .then((response) => {
          this.showSuccessMessage('Der AV-Vertrag wurde als neue Version angelegt.');
          this.params.id = response.data.id;
          this.fetchData();
        })
        .catch(() => {
          this.showErrorMessage('Das Speichern ist fehlgeschlagen.');
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    onNewVersionRefuse() {
      this.isNewVersionConfirmDialogDisplayed = false;
      this.save();
    },
  },
};
</script>

<template>
  <div class="avContract-detail">
    <v-progress-linear v-if="isLoading" :indeterminate="true"/>

    <span
      v-else-if="hasError"
      class="js-error ma-3"
    >An error occurred while fetching avContract information</span>

    <template v-else>
      <v-toolbar dark dense height="40" color="blue-grey lighten-2">

        <v-tooltip bottom>
          <v-btn @click="fetchData" icon slot="activator">
            <v-icon>refresh</v-icon>
          </v-btn>
          <span>Aktualisieren</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn
            @click="isNewVersionConfirmDialogDisplayed = true"
            slot="activator"
            :loading="isSaving"
            icon
          >
            <v-icon>save</v-icon>
          </v-btn>
          <span>Speichern</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn
            v-if="auth.hasDownloadRights"
            slot="activator"
            :disabled="isDownloading"
            flat icon
            @click.native="_downloadReport"
          >
            <v-icon small>cloud_download</v-icon>
          </v-btn>
          <span>Bericht herunterladen</span>
        </v-tooltip>
      </v-toolbar>

      <v-form>
        <v-container fluid grid-list-lg pt-2 pa-0>
          <v-tabs v-model="activeTab" height="35" slider-color="black" color="amber">

            <v-tab ripple>Basisdaten</v-tab>
            <v-tab ripple>Gegenstand und Dauer</v-tab>
            <v-tab ripple>Auftragsinhalt</v-tab>
            <v-tab ripple>Art der Daten</v-tab>
            <v-tab ripple>Qualitätssicherung</v-tab>
            <v-tab ripple>Unterauftragsverhältnisse</v-tab>
            <v-tab ripple>Kontrollrechte</v-tab>
            <v-tab ripple>Prüfung</v-tab>
            <v-tab ripple>Dokumente</v-tab>
            <v-tab ripple>Notizen</v-tab>
            <v-tab ripple>Historie</v-tab>

            <v-tab-item>
              <AvContractBasicInfoForm :av-contract="avContract" @save="save" />
            </v-tab-item>
            <v-tab-item>
              <AvContractObjectAndDurationForm :av-contract="avContract" @save="save" />
            </v-tab-item>
            <v-tab-item>
              <AvContractJobContentForm :av-contract="avContract" @save="save" />
            </v-tab-item>
            <v-tab-item>
              <AvContractTypeOfDataForm :av-contract="avContract" @save="save" />
            </v-tab-item>
            <v-tab-item>
              <AvContractQualityControlForm :av-contract="avContract" @save="save" />
            </v-tab-item>
            <v-tab-item>
              <AvContractSubcontractToForm :av-contract="avContract" @save="save" />
            </v-tab-item>
            <v-tab-item>
              <AvContractControlRightsForm :av-contract="avContract" @save="save" />
            </v-tab-item>
            <v-tab-item>
              <AvContractExamForm :av-contract="avContract" @save="save" />
            </v-tab-item>
            <v-tab-item>
              <Documents
                :resource="'avContract'" :id="avContract.id"
                :allowed-file-types="['csv', 'doc', 'pdf', 'rtf', 'tex', 'txt', 'xlsx']"
              />
            </v-tab-item>
            <v-tab-item>
              <Notes
                :parent-resource="'avContract'"
                :parent-resource-id="avContract.id"
                @save="save" path="/notes"
              />
            </v-tab-item>
            <v-tab-item>
              <History :domain="'avContract'" :domain-id="avContract.id" path="/history" />
            </v-tab-item>
          </v-tabs>
        </v-container>
      </v-form>

      <rbx-confirm-dialog
        @confirm="onNewVersionConfirm" @refuse="onNewVersionRefuse"
        :message="'Sollen die Änderungen als neue Version dieses AV-Vertrags gespeichert werden?'"
        :is-displayed="isNewVersionConfirmDialogDisplayed"
        :is-loading="isLoading"
      />
    </template>
  </div>
</template>

<style lang="scss">
.avContract-detail {
  &-title {
    margin-bottom: 10px;
  }

  &-actions {
    margin: 5px 0;

    .v-btn:first-child {
      border-radius: 5px 0 0 5px;
    }

    .v-btn:last-child {
      border-radius: 0 5px 5px 0;
    }

    .v-btn + .v-btn {
      border-left: 1px solid #fff !important;
    }

    .v-btn {
      margin: 0;
      width: 35px;
      height: 35px;
      border-radius: 0px;
    }
  }

  &-tab {
    padding-top: 10px;

    &-title {
      margin-bottom: 10px;
    }
  }

  &-common-list {
    margin-top: 5px;
    .v-btn {
      margin: 0px;
      width: 30px;
      height: 30px;
    }

    &-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }

    &-item {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &:hover {
        .v-btn {
          opacity: 1;
        }
      }

      .v-btn {
        opacity: 0;
      }
    }
  }

  .v-tabs {
    &__items {
      padding-bottom: 30px;
    }
  }

  .submit-button {
    margin-top: 20px;
  }
}
</style>
